// MarketplaceTemplateForm
import React, { useEffect, useState } from 'react'
import { useStyles } from './MarketplaceTemplateForm.styles'
import {
  ButtonV2,
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  ModalConfirmV2,
  ModalSizeVariants,
  TextInputV2,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen } from '@fortawesome/pro-regular-svg-icons'
import HubspotImage from '../../../common/images/HubSpot_Logo.svg'
import SalesforceImage from '../../../common/images/salesforce.svg'
import { MarketplaceTemplateFormFooter } from './MarketplaceTemplateFormFooter/MarketplaceTemplateFormFooter'
import { MarketplaceTemplateTableContainer } from './MarketplaceTemplateTableContainer/MarketplaceTemplateTableContainer'
import { MarketplaceTemplateTableContainerSalesforce } from './MarketplaceTemplateTableContainer/MarketplaceTabConfigurationWrapper/MarketplaceTemplateTabContainerWrapperSalesforce'
import { useDispatch, useSelector } from '../../../store'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useUserType, useAccountId } from '@labrav/common'
import { isEmpty } from 'lodash'
import {
  cloneMarketplaceReferralTemplate,
  deleteMarketplaceReferralTemplate,
  getAllMarketplaceHubspotFields,
  getAllSalesforceFields,
  getMarketplaceReferralTemplate,
  getMarketplaceReferralTemplateSalesforce,
  updateMarketplaceReferralTemplateName,
} from '../../../modules/marketplaceMappingManager/action'
import { LoadingStateComponent } from '@labrav/common'
import { CircularProgress } from '@mui/material'
import { useTracker } from '@labrav/common'
import { snakeize } from 'casing'
import { UserProfile } from '@labrav/common'
import { ShortenedNameV2 } from '@labrav/common'
import { CRMTypes } from '../../../utils/constants'
import { CloudType } from '../../../types/external'
import { LoadingTypes, isLoading as Loading } from '@labrav/common'

const ColumnActionWithMenuOptions = [
  {
    key: 'clone-template',
    label: 'Clone',
  },
  {
    key: 'delete-template',
    label: 'Delete',
  },
  {
    key: 'view-activity-log',
    label: 'View activity log',
    disable: true,
    disabledTooltip: 'Coming soon',
  },
]

export const MarketplaceTemplateForm: React.FC = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isViewOnly = queryParams.get('viewOnly') === 'true'
  const { classes } = useStyles({ isViewOnly })
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const partnerType = useUserType()
  const tracker = useTracker()
  const { templateId = '' } = useParams()
  const crmId = useSelector<string>(
    state => state.AccountData[partnerType]?.partnerData?.crmId || ''
  )
  const crmType = useSelector<string>(
    state => state.AccountData[partnerType]?.partnerData?.crmType || ''
  )

  const { cloudType } = useParams<{
    cloudType?: string
  }>()
  const cloudForAccountLookup = (cloudType || 'aws').toUpperCase() as CloudType
  const accountId =
    useAccountId({
      product: 'flyout',
      cloud: cloudForAccountLookup,
    }) || ''
  const user = useSelector<UserProfile | undefined>(
    state => state.userProfile.userProfile
  )
  const username = `${user?.givenName || ''} ${user?.familyName || ''}`.trim()

  const currentTemplateData = useSelector(
    state => state.marketplaceMappingManager.templateData.templateData
  )
  const partnerUserId = useSelector<string | undefined>(
    state => state.userProfile.userProfile.id
  ) as string

  const isEditTemplateNameLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.EDIT_REFERRAL_TEMPLATE_NAME)
  )
  const isReferralTemplatePageLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
  )
  const isHubspotFieldDataLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_HUBSPOT_FIELDS)
  )
  const isSalesforceFieldDataLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_CRM_FIELDS)
  )
  const isDiscardLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.DISCARD_REFERRAL_TEMPLATE)
  )

  const [openEditTemplateModal, setOpenEditTemplateModal] =
    useState<boolean>(false)
  const [editTemplateName, setEditTemplateName] = useState('')
  const [editNameError, setEditNameError] = useState('')
  const [acceptDisabled, setAcceptDisabled] = useState(false)
  const isSalesforce = crmType === CRMTypes.salesforce
  const isHubspot = crmType === CRMTypes.hubspot

  const trackEvent = ({
    action,
    title,
    section = 'Marketplace Templates',
    component,
    customData = {},
  }: {
    action: string
    title: string
    section?: string
    component?: string
    customData?: Record<string, any>
  }) => {
    const snakeCaseSectionName = snakeize(section)
    const snakeCaseComponentName = snakeize(
      component || 'MarketplaceTemplateForm'
    )
    tracker?.track({
      section: `marketplace_mapping_manager-${snakeCaseSectionName}`,
      component: snakeCaseComponentName,
      action,
      title,
      partnerId: `${crmId}`,
      customerName: `${username}`,
      ...customData,
    })
  }

  const handleCloseEditTemplateModal = () => {
    setOpenEditTemplateModal(false)
    setEditTemplateName('')
    setEditNameError('')
  }
  const handleEditTemplateIconClick = () => {
    trackEvent({
      action: 'edit-template-icon-click',
      title: 'Edit Template Icon Click',
    })
    setEditTemplateName(currentTemplateData?.name)
    setOpenEditTemplateModal(true)
  }

  const handleEditNameBlur = () => {
    if (!editTemplateName.trim()) {
      setEditNameError('Template name is required.')
      setAcceptDisabled(true)
    }
  }

  const handleEditNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEditTemplateName(value)
    if (!value.trim()) {
      setEditNameError('Template name is required.')
      setAcceptDisabled(true)
    } else if (value.length > 255) {
      setEditNameError('Template name must be less than 255 characters.')
      setAcceptDisabled(true)
    } else {
      setEditNameError('')
      setAcceptDisabled(false)
    }
  }

  const handleSaveEditTemplate = async () => {
    const isNameEdited = (await dispatch(
      updateMarketplaceReferralTemplateName(
        crmId,
        currentTemplateData.id,
        {
          name: editTemplateName,
          ...(crmType === CRMTypes.salesforce
            ? { updatedBy: partnerUserId }
            : {}),
        },
        accountId,
        crmType === CRMTypes.salesforce
          ? !!accountId
          : currentTemplateData?.useTemplateFromMappingService,
        crmType === CRMTypes.salesforce
      )
    )) as unknown as boolean
    if (isNameEdited) {
      setOpenEditTemplateModal(false)
      trackEvent({
        action: 'save-template-name',
        title: 'Save Template Name',
        component: 'EditTemplateModal',
        customData: { newName: editTemplateName },
      })
    } else {
      setEditNameError('Template already exists. Please enter a unique name.')
      setOpenEditTemplateModal(true)
    }
  }

  const handleSelectOption = async (option: ColumnMenuOption) => {
    if (option.key === 'clone-template') {
      const { templateId } = (await dispatch(
        cloneMarketplaceReferralTemplate(
          {
            name: `Clone_${currentTemplateData.name}`,
            templateType: 'flyout',
            createdBy: partnerUserId,
          },
          crmId,
          currentTemplateData.id || ''
        )
      )) as unknown as { templateId: string; errorMessage: string }
      if (!isEmpty(templateId)) {
        trackEvent({
          action: 'clone-template',
          title: 'Template Cloned',
          component: 'ColumnActionWithMenu',
          customData: { templateId },
        })
        navigate(`/settings/mappings/templates/${templateId}`)
      }
    } else if (option.key === 'delete-template') {
      await dispatch(
        deleteMarketplaceReferralTemplate(
          crmId,
          currentTemplateData.id || '',
          currentTemplateData.name || ''
        )
      )
      trackEvent({
        action: 'delete-template',
        title: 'Delete Template Clicked',
        component: 'ColumnActionMenu',
      })
      navigate(`/settings/mappings/marketplace`)
    }
  }

  useEffect(() => {
    if (!isEmpty(crmId)) {
      if (isSalesforce && accountId) {
        dispatch(getAllSalesforceFields(crmId, accountId))
        dispatch(
          getMarketplaceReferralTemplateSalesforce(crmId, accountId, templateId)
        )
      } else if (isHubspot) {
        dispatch(getAllMarketplaceHubspotFields(crmId))
        dispatch(getMarketplaceReferralTemplate(crmId, templateId))
      }
    }
  }, [accountId, crmId, isHubspot, isSalesforce, templateId])

  if (
    isHubspotFieldDataLoading ||
    isSalesforceFieldDataLoading ||
    isReferralTemplatePageLoading
  ) {
    return (
      <LoadingStateComponent
        heading="Loading your marketplace template form page"
        description="It may take a few moments, please wait..."
      />
    )
  }

  return (
    <div
      className={classes.root}
      data-testid="marketplace-template-form-container"
    >
      <div
        className={classes.topContainer}
        data-testid="marketplace-template-top-container"
      >
        <div className={classes.templateNameContainer}>
          <div className={classes.name}>
            <ShortenedNameV2
              name={currentTemplateData?.name}
              length={20}
              showClipboardTooltip={true}
            />
          </div>
          <ButtonV2
            styleType="link"
            data-testid={'edit-template-name-btn'}
            smallVariant
            onClick={handleEditTemplateIconClick}
            disabled={isViewOnly}
          >
            <FontAwesomeIcon icon={faPen} className={classes.icon} />
          </ButtonV2>
        </div>
        <img
          src={isHubspot ? HubspotImage : SalesforceImage}
          alt={isHubspot ? 'Hubspot Icon' : 'Salesforce Icon'}
          className={classes.hubspotIcon}
        />
      </div>
      <div
        className={classes.middleContainer}
        data-testid="marketplace-template-middle-container"
      >
        <div
          className={classes.columnActionContainer}
          data-testid="column-action-menu"
        >
          <ColumnActionWithMenuV2
            handleSelectOption={handleSelectOption}
            menuOptions={ColumnActionWithMenuOptions}
            disable={isViewOnly}
          />
        </div>
      </div>
      <div
        className={classes.bottomContainer}
        data-testid="marketplace-template-bottom-container"
      >
        {isDiscardLoading ? (
          <CircularProgress className={classes.loadingSpinner} />
        ) : (
          <>
            {isSalesforce ? (
              <MarketplaceTemplateTableContainerSalesforce />
            ) : (
              <MarketplaceTemplateTableContainer />
            )}
          </>
        )}
      </div>
      <MarketplaceTemplateFormFooter />
      <ModalConfirmV2
        title="Edit template name"
        icon={
          <FontAwesomeIcon
            icon={faPen}
            data-testid="reset-mapping-icon-modal"
          />
        }
        onlyCloseModal
        onCloseModal={handleCloseEditTemplateModal}
        onDecline={handleCloseEditTemplateModal}
        content={
          <div>
            <TextInputV2
              keyName="edit-template-name"
              title="Enter a unique template name"
              placeHolder=""
              type="text"
              value={editTemplateName}
              onChange={handleEditNameChange}
              onBlur={handleEditNameBlur}
              isError={!!editNameError}
              helperText={!!editNameError}
              showSpinner={false}
              errorText={editNameError}
              isReadOnly={false}
              isRequired={true}
            />
          </div>
        }
        open={openEditTemplateModal}
        titleCancelButton={'Cancel'}
        titleAcceptButton={'Save'}
        onAccept={handleSaveEditTemplate}
        variant={ModalSizeVariants.medium}
        hideCancelButton={false}
        acceptDisabled={acceptDisabled}
        acceptLoading={isEditTemplateNameLoading}
      />
    </div>
  )
}
